/**
 * product-sticky-atc.js — mobile sticky Add to Cart bar.
 *
 * Shows once the real Add to Cart button leaves the viewport and hides again
 * when it returns, so the two are never on screen together.
 *
 * The bar's button is a submit for the main product form, so adding to cart,
 * variant selection, the drawer and analytics are all the theme's existing
 * code path — this file only decides when the bar is visible and keeps the
 * label, price and disabled state in step with the real button.
 *
 * Standalone asset, decoupled from theme.dev.js.
 */
(function () {
  'use strict';

  class ProductStickyAtc extends HTMLElement {
    constructor() {
      super();
      this.observer = null;
      this.mirror = null;
    }

    connectedCallback() {
      this.form = document.getElementById(this.getAttribute('data-form'));
      if (!this.form) return;

      this.button = this.querySelector('[data-sticky-atc-button]');
      this.label = this.querySelector('[data-sticky-atc-label]');
      this.price = this.querySelector('[data-sticky-atc-price]');

      // The real button is both the trigger and the source of truth for state.
      this.anchor = this.form.querySelector('[data-add-to-cart]');
      if (!this.anchor) return;

      this.watchVisibility();
      this.watchAnchorState();
      this.syncFromAnchor();

      // Forward to the real button so the theme's own add-to-cart runs. Its
      // click listener resolves the form via closest('form'), which this bar
      // sits outside of, so submitting across forms would just navigate.
      if (this.button) {
        this.button.addEventListener('click', (event) => {
          event.preventDefault();
          if (this.anchor.disabled) return;
          this.anchor.click();
        });
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.mirror) this.mirror.disconnect();
    }

    watchVisibility() {
      if (!('IntersectionObserver' in window)) return;

      // Not intersecting means the button is off screen in EITHER direction, so
      // it is also true at the top of the page before the button is reached.
      // Only show the bar once the button has gone past the top of the viewport.
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const scrolledPast = entry.boundingClientRect.bottom <= 0;
            this.toggle(!entry.isIntersecting && scrolledPast);
          });
        },
        { threshold: 0 }
      );

      this.observer.observe(this.anchor);
    }

    // Sold out, unavailable and "Adding..." states all land on the real button.
    watchAnchorState() {
      if (!window.MutationObserver) return;

      this.mirror = new MutationObserver(() => this.syncFromAnchor());
      this.mirror.observe(this.anchor, {
        attributes: true,
        attributeFilter: ['disabled', 'class'],
        childList: true,
        subtree: true,
      });

      const priceSource = this.form.querySelector('[data-product-price]');
      if (priceSource) {
        this.priceMirror = new MutationObserver(() => this.syncPrice(priceSource));
        this.priceMirror.observe(priceSource, { childList: true, subtree: true, characterData: true });
      }
    }

    syncFromAnchor() {
      if (this.button) this.button.disabled = this.anchor.disabled;

      const text = this.anchor.querySelector('[data-add-to-cart-text]');
      if (text && this.label) {
        // The real label may carry the price too; the bar shows that separately.
        const own = text.cloneNode(true);
        own.querySelectorAll('[data-product-price], .product__price--regular').forEach((n) => n.remove());
        const clean = own.textContent.replace(/[\s·•|-]+$/, '').trim();
        if (clean) this.label.textContent = clean;
      }

      const priceSource = this.form.querySelector('[data-product-price]');
      if (priceSource) this.syncPrice(priceSource);
    }

    syncPrice(source) {
      if (!this.price) return;
      const value = source.textContent.trim();
      if (value) this.price.textContent = value;
    }

    toggle(show) {
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
    }
  }

  if (!customElements.get('product-sticky-atc')) {
    customElements.define('product-sticky-atc', ProductStickyAtc);
  }
})();
